import styled from 'styled-components';
import type { IconCTA } from '@amplience/content-types/typings/c-icon-&-cta';
import { ImagePartial } from '@storefront/lib-global/partials/image';
import { LinkPartial } from '@storefront/lib-global/partials/link';
import { RichTextPartial } from '@storefront/lib-global/partials/richText';
import { TypographyStyleMapping, TypographyStyles, colours, media, spacing } from '@storefront/lib-global/stylings';
import { getLocalizedValue } from '@storefront/lib-global/utils/transformers';

export type IconAndCTAProps = IconCTA;

const S = {
  Background: styled.div<{ $bgColor?: string }>`
    align-items: center;
    background-color: ${({ $bgColor }) => $bgColor};
    border: none;
    display: flex;
    gap: ${spacing.XXXS};
    min-height: 52px;
    padding: 0 ${spacing.S};
    width: 100%;

    @media ${media.greaterThan('lg')} {
      min-height: 71px;
      padding: 0 ${spacing.L};
    }
  `,
  IconContainer: styled.div`
    flex-shrink: 0;
    height: 18px;
    position: relative;
    width: 18px;
  `,
  CTA: styled(LinkPartial)<{ $textColor: string; $textSize: string }>`
    ${({ $textSize }) => ($textSize ? TypographyStyleMapping[$textSize] : null)}
    color: ${({ $textColor }) => $textColor};
    word-break: break-word;
  `,
  IconAndCTAContainer: styled.div`
    margin: 0 -${spacing.S};
    width: auto;

    @media ${media.greaterThan('lg')} {
      margin: unset;
      width: 333px;
    }
  `,
  RichText: styled(RichTextPartial)`
    margin: 0 ${spacing.S};
    word-break: break-word;
    a {
      ${TypographyStyles.CallToAction.Large.SemiBold}
      color: ${colours.BLACK};
    }

    @media ${media.greaterThan('lg')} {
      margin: 0;
    }
  `,
};

export function IconAndCta({
  richTextAboveIconCTA,
  iconCTABackgroundColor,
  CTA,
  icon,
  ctaTextColor,
  ctaTextSize,
}: IconAndCTAProps) {
  return (
    <S.IconAndCTAContainer>
      <S.RichText
        fontSize={TypographyStyles.Body.Small.Regular}
        text={getLocalizedValue(richTextAboveIconCTA?.richText)}
      />
      <S.Background $bgColor={iconCTABackgroundColor}>
        <S.IconContainer>
          {icon?.image && (
            <ImagePartial
              layout="intrinsic"
              altText={icon.altText}
              src={icon.image.name ?? ''}
              assetType={icon.assetType}
              height={icon.image && 18}
              width={icon.image && 18}
              defaultHost={icon.image.defaultHost}
              useOriginalSize={icon.useOriginalSize}
            />
          )}
        </S.IconContainer>
        <S.CTA $textSize={ctaTextSize} $textColor={ctaTextColor} {...CTA?.link}>
          {getLocalizedValue(CTA?.text)}
        </S.CTA>
      </S.Background>
    </S.IconAndCTAContainer>
  );
}
